import { useState } from 'react';
import { api } from '@/lib/api-client';
import type { Country, Profile } from '@/types';
import { isGerman } from '@/i18n/locale';
import { Icon } from '@/components/ui/icons';

// CountryEdit — Phase 5 step 1.
//
// Radio for the supported countries (DE / AT / CH / US + Other).
// US joined in Oct 2026 (migration 014). Auto-detection runs once at
// app load (App.tsx); this component is the manual-override surface.
// Mounted on /profile immediately below MotherProfileSection.
//
// Optimistic UI: click → local state updates immediately and the PATCH
// fires in the background. On error we revert and surface the message.

interface Props {
  initial: Country | null;
  onSaved: (profile: Profile) => void;
}

// Country codes instead of flag emoji: flags render as plain letters on
// Windows and in several Android keyboards, so the code is the reliable
// glyph and matches how the picker is spoken by a screen reader.
const OPTIONS: Array<{ v: Country; en: string; de: string }> = [
  { v: 'DE',    en: 'Germany',     de: 'Deutschland' },
  { v: 'AT',    en: 'Austria',     de: 'Österreich' },
  { v: 'CH',    en: 'Switzerland', de: 'Schweiz' },
  { v: 'US',    en: 'United States', de: 'USA' },
  { v: 'OTHER', en: 'Other',       de: 'Andere' },
];

export function CountryEdit({ initial, onSaved }: Props) {
  const de = isGerman();
  const [current, setCurrent] = useState<Country | null>(initial);
  const [saving, setSaving] = useState<Country | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const onPick = async (v: Country) => {
    if (v === current) return;
    const previous = current;
    setCurrent(v); // optimistic
    setSaving(v);
    setErr(null);
    try {
      const { profile } = await api.profile.updateMother({ country: v });
      onSaved(profile);
    } catch (e) {
      setCurrent(previous); // revert
      setErr((e as Error).message ?? (de ? 'Speichern fehlgeschlagen.' : 'Failed to save.'));
    } finally {
      setSaving(null);
    }
  };

  return (
    <section className="card p-5">
      <h2 className="font-display font-semibold text-slate">
        {de ? 'Land' : 'Country'}
      </h2>
      <p className="text-xs text-slate-muted mt-1 leading-relaxed">
        {de
          ? 'Wird verwendet für lokale Empfehlungen wie Impfpläne.'
          : 'Used for local recommendations like vaccination schedules.'}
      </p>

      <div className="mt-4 grid grid-cols-2 gap-2" role="radiogroup" aria-label={de ? 'Land' : 'Country'}>
        {OPTIONS.map((opt) => {
          const on = current === opt.v;
          const isSaving = saving === opt.v;
          return (
            <button
              key={opt.v}
              type="button"
              role="radio"
              aria-checked={on}
              aria-busy={isSaving || undefined}
              onClick={() => onPick(opt.v)}
              disabled={saving !== null}
              className={'choice-segment justify-start' + (opt.v === 'OTHER' ? ' col-span-2' : '')}
            >
              <span
                className={
                  'shrink-0 w-7 h-5 rounded text-[10px] font-semibold tracking-wide flex items-center justify-center ' +
                  (on ? 'bg-white/20 text-white' : 'bg-primary-50 text-primary-700')
                }
                aria-hidden
              >
                {opt.v === 'OTHER' ? <Icon.Globe size={14} /> : opt.v}
              </span>
              <span className="flex-1 text-left">{de ? opt.de : opt.en}</span>
              {isSaving && <span className="text-[10px] opacity-80">…</span>}
            </button>
          );
        })}
      </div>

      {err && (
        <div className="mt-3 rounded-lg bg-danger/5 border border-danger/30 px-3 py-2 text-sm text-danger-700">
          {err}
        </div>
      )}
    </section>
  );
}
